import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, FileText, Lock, Tag, Wallet } from "lucide-react";
import { useDraftStore } from "@/shared/store/draft";
import { useBloggers, useCreateCampaign, useLaunchCampaign, usePatchCampaign } from "@/shared/api/wb";
import { ApiError } from "@/shared/api/client";
import { Avatar, Card, Field, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { StepHead } from "../StepHead";
import { WizardLayout } from "../WizardLayout";
import { setWizardModerateHandler } from "@/app/WizardStepper";

const FEE_PCT = 7;
const PROMO_CODES: Record<string, number> = { TEST10: 10 };

function toUserError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status >= 500) return "Ошибка сервера. Попробуйте через несколько секунд.";
    if (e.status === 404) return "Кампания не найдена. Вернитесь на предыдущий шаг.";
    if (e.status === 401 || e.status === 403) return "Сессия истекла — перезайдите в аккаунт.";
    if (e.status === 400 || e.status === 422) return "Некорректные данные кампании. Проверьте заполненные поля.";
  }
  if (e instanceof TypeError) return "Нет соединения с сервером. Проверьте интернет.";
  return "Не удалось запустить кампанию. Попробуйте ещё раз или напишите в поддержку.";
}

export function PaymentStep() {
  const navigate = useNavigate();
  const { draft, branch, patchDraft, setBranch, setActiveCampaign, reset } = useDraftStore();
  const { data: bloggers } = useBloggers({});
  const createCampaign = useCreateCampaign();
  const patchCampaign = usePatchCampaign();
  const launchCampaign = useLaunchCampaign();

  const [submitting, setSubmitting] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Promo code
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState<number | null>(null); // discount %
  const [promoError, setPromoError] = useState<string | null>(null);

  // Advertiser details
  const [advertiserName, setAdvertiserName] = useState("ООО «WOWBLOGGER»");
  const [advertiserInn, setAdvertiserInn] = useState("7724444555");
  const [advertiserEditing, setAdvertiserEditing] = useState(!draft.advertiserDataSaved);

  // Invoice email
  const [invoiceEmail, setInvoiceEmail] = useState("finance@example.ru");

  const items = (bloggers ?? []).filter((b) => draft.selectedBloggerIds.includes(b.id));
  const total = items.reduce((s, b) => {
    const fmts = draft.bloggerFormats[b.id] ?? [];
    return s + (fmts.length > 0 ? fmts.reduce((n, f) => n + f.price, 0) : b.price);
  }, 0);
  const reach = items.reduce((s, b) => s + b.reach, 0);
  const fee = Math.round(total * FEE_PCT / 100);
  const discount = promoApplied !== null ? Math.round(total * promoApplied / 100) : 0;
  const finalAmount = total + fee - discount;

  function applyPromo() {
    const code = promoInput.trim().toUpperCase();
    const pct = PROMO_CODES[code];
    if (pct !== undefined) {
      setPromoApplied(pct);
      setPromoError(null);
    } else {
      setPromoApplied(null);
      setPromoError("Промокод не найден или уже использован.");
    }
  }

  const onPayRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => {
    setWizardModerateHandler(() => { onPayRef.current(); });
    return () => setWizardModerateHandler(() => {});
  }, []);

  onPayRef.current = onPay;
  async function onPay() {
    if (submitting || items.length === 0) return;
    setSubmitting(true);
    setLaunchError(null);
    patchDraft({ advertiserDataSaved: true });
    try {
      const fresh = await createCampaign.mutateAsync({
        type: branch.campaignType,
        product_url: draft.productUrl || undefined,
      });
      await patchCampaign.mutateAsync({
        id: fresh.id,
        patch: {
          product: draft.productName || "Новая кампания",
          product_desc: draft.productDesc || "",
          product_category: draft.productCategory || null,
          product_type: draft.productType || null,
          geo: draft.geo,
          budget: draft.budget,
          selected_blogger_ids: draft.selectedBloggerIds,
          creative_channel_map: draft.creativeChannelMap,
          creative_payload: {
            seedingText: draft.creative.seedingText,
            nativeBrief: draft.creative.nativeBrief,
            variants: draft.variants ?? [],
          },
          payment_method: branch.payment,
          advertiser_data: { name: advertiserName, inn: advertiserInn },
        },
      });
      const launched = await launchCampaign.mutateAsync({
        id: fresh.id, method: branch.payment,
        utmPerBlogger: draft.utmPerBlogger,
        placementDates: draft.placementDates,
      });
      setActiveCampaign(launched.campaign_id);
      reset();
      navigate(`/campaign/${launched.campaign_id}?launched=1`);
    } catch (e) {
      setLaunchError(toUserError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <WizardLayout>
      <StepHead step="05" title="Оплата" sub="Приглашения блогерам уйдут только после оплаты. Если кто-то откажется — вернём деньги или найдём замену." />

      <div className="grid grid-cols-[1.4fr_1fr] gap-6 items-start">
        <div className="grid gap-4">
          {/* Blogger list */}
          <Card className="!p-0">
            <div className="px-6 py-4 border-b border-ink-12 flex items-baseline justify-between">
              <div className="font-display font-bold text-[18px]">Блогеры в кампании</div>
              <div className="text-[12px] text-ink-40">{items.length} каналов</div>
            </div>
            <div className="py-1">
              {items.length === 0 && (
                <div className="px-6 py-8 text-center text-ink-40">Корзина пуста — вернитесь и добавьте блогеров.</div>
              )}
              {items.map((b) => {
                const fmts = draft.bloggerFormats[b.id] ?? [];
                const itemPrice = fmts.length > 0 ? fmts.reduce((n, f) => n + f.price, 0) : b.price;
                return (
                  <div key={b.id} className="grid grid-cols-[36px_1fr_auto_auto] gap-3 items-center px-6 py-2.5 border-b border-ink-12 last:border-0">
                    <Avatar name={b.full} size={36} grad={b.grad as [string, string]} />
                    <div>
                      <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
                        <PlatformIcon platform={b.platform as Platform} size={12} /> {b.name}
                      </div>
                      <div className="text-[12px] text-ink-40">{fmtNum(b.reach)} охват · CPV {b.cpv}₽</div>
                    </div>
                    <div className="font-mono text-[13px] text-ink-40">{fmts.length > 1 ? `×${fmts.length}` : "×1"}</div>
                    <div className="font-mono font-bold text-[13.5px]">{fmtRub(itemPrice)}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Payment method */}
          <Card className="!p-6">
            <div className="font-display font-bold text-[18px] mb-4">Способ оплаты</div>
            <div className="grid grid-cols-2 gap-2.5">
              <MethodCard active={branch.payment === "card"} onClick={() => setBranch("payment", "card")} icon={<Wallet size={18} />} title="Банковская карта" sub="Мгновенно" />
              <MethodCard active={branch.payment === "invoice"} onClick={() => setBranch("payment", "invoice")} icon={<FileText size={18} />} title="По счёту" sub="Счёт придёт на email сразу" />
            </div>
            {branch.payment === "card" && (
              <div className="mt-4 grid gap-2.5 fade-up">
                <input placeholder="4242 4242 4242 4242" className="h-11 px-4 rounded-md border border-ink-12 bg-white font-mono text-[14px] outline-none focus:border-ink" />
                <div className="grid grid-cols-3 gap-2.5">
                  <input placeholder="MM/YY" className="h-11 px-4 rounded-md border border-ink-12 bg-white font-mono text-[14px] outline-none focus:border-ink" />
                  <input placeholder="CVC"   className="h-11 px-4 rounded-md border border-ink-12 bg-white font-mono text-[14px] outline-none focus:border-ink" />
                  <input placeholder="Имя"   className="h-11 px-4 rounded-md border border-ink-12 bg-white       text-[14px] outline-none focus:border-ink" />
                </div>
              </div>
            )}
            {branch.payment === "invoice" && (
              <div className="mt-4 grid gap-2.5 fade-up">
                <Field
                  label="Email для счёта"
                  value={invoiceEmail}
                  onChange={(e) => setInvoiceEmail(e.target.value)}
                />
                <div className="flex items-center gap-2 p-3 rounded-md bg-ink-6 text-ink-60 text-[12.5px]">
                  <CheckCircle2 size={14} className="text-[#639922] flex-shrink-0" />
                  Счёт будет отправлен на {invoiceEmail || "email"} сразу после подтверждения. Приглашения блогерам уйдут после оплаты счёта.
                </div>
              </div>
            )}
          </Card>

          {/* Advertiser details */}
          <Card className="!p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-display font-bold uppercase tracking-[0.16em] text-ink-40">Реквизиты</div>
              {!advertiserEditing && (
                <button
                  onClick={() => setAdvertiserEditing(true)}
                  className="text-[12px] text-link bg-transparent border-0 cursor-pointer hover:underline"
                >
                  Изменить
                </button>
              )}
            </div>
            {advertiserEditing ? (
              <div className="grid gap-2 fade-up">
                <input
                  value={advertiserName}
                  onChange={(e) => setAdvertiserName(e.target.value)}
                  placeholder="Название организации"
                  className="h-9 px-3 rounded-md border border-ink-12 bg-white text-[13.5px] outline-none focus:border-ink"
                />
                <input
                  value={advertiserInn}
                  onChange={(e) => setAdvertiserInn(e.target.value)}
                  placeholder="ИНН"
                  className="h-9 px-3 rounded-md border border-ink-12 bg-white font-mono text-[13px] outline-none focus:border-ink"
                />
                <button
                  onClick={() => { patchDraft({ advertiserDataSaved: true }); setAdvertiserEditing(false); }}
                  disabled={!advertiserName.trim() || !advertiserInn.trim()}
                  className="h-9 rounded-md bg-ink text-white text-[13px] font-display font-semibold disabled:opacity-40"
                >
                  Сохранить
                </button>
              </div>
            ) : (
              <>
                <div className="font-display font-semibold text-[13.5px]">{advertiserName}</div>
                <div className="text-[12px] text-ink-40 font-mono">ИНН {advertiserInn}</div>
              </>
            )}
          </Card>
        </div>

        {/* Right column: summary */}
        <div className="sticky top-6 grid gap-4">
          <Card className="!p-6">
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.16em] text-ink-40 mb-3">Итого по РК</div>
            <div className="grid gap-2 text-[13.5px] mb-3.5">
              <SumRow k="Размещения" v={fmtRubFull(total)} />
              <SumRow k={`Сервисный сбор ${FEE_PCT}%`} v={fmtRubFull(fee)} />
              {discount > 0 && (
                <SumRow k={`Промокод −${promoApplied}%`} v={`−${fmtRubFull(discount)}`} accent />
              )}
              <SumRow k="НДС" v="включено" muted />
            </div>
            <div className="flex justify-between items-baseline pt-3.5 border-t border-ink-12">
              <div className="text-[12.5px] text-ink-40">К оплате</div>
              <div className="font-display font-extrabold text-[22px]">{fmtRubFull(finalAmount)}</div>
            </div>
            <div className="text-[12px] text-ink-40 mt-2">
              Прогноз <b className="text-ink">~{fmtNum(reach)}</b> просмотров · CPV ≈ <b className="text-ink">{reach ? (finalAmount / reach).toFixed(2) : "0.00"} ₽</b>
            </div>

            {/* Promo code */}
            <div className="mt-4 pt-4 border-t border-ink-12">
              {promoApplied !== null ? (
                <div className="flex items-center gap-2 text-[12.5px] text-[#27500A]">
                  <CheckCircle2 size={14} className="flex-shrink-0" />
                  Промокод применён: скидка {promoApplied}%
                  <button onClick={() => { setPromoApplied(null); setPromoInput(""); }} className="ml-auto text-ink-40 hover:text-ink text-[11px]">Убрать</button>
                </div>
              ) : (
                <div className="grid gap-1.5">
                  <div className="text-[11px] font-display font-bold uppercase tracking-[0.1em] text-ink-40">Промокод</div>
                  <div className="flex gap-2">
                    <input
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(null); }}
                      onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      placeholder="TEST10"
                      className="flex-1 h-9 px-3 rounded-md border border-ink-12 bg-white font-mono text-[13px] outline-none focus:border-ink uppercase placeholder:normal-case placeholder:text-ink-30"
                    />
                    <button
                      onClick={applyPromo}
                      disabled={!promoInput.trim()}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-ink-6 text-ink text-[12.5px] font-display font-semibold hover:bg-ink-12 disabled:opacity-40"
                    >
                      <Tag size={12} /> Применить
                    </button>
                  </div>
                  {promoError && <div className="text-[11.5px] text-danger">{promoError}</div>}
                </div>
              )}
            </div>
          </Card>

          <Card className="!p-4 bg-lime border-lime text-[var(--fg-on-accent)]">
            <div className="flex gap-3 items-start">
              <Lock size={18} />
              <div>
                <div className="font-display font-bold text-[13.5px] mb-1">Гарантия выхода</div>
                <div className="text-[12.5px] opacity-85 leading-relaxed">
                  Если блогер не выйдет — мы вернём деньги или найдём замену.
                </div>
              </div>
            </div>
          </Card>

          {launchError && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-md bg-danger/10 text-danger text-[13px] leading-snug">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              <span>{launchError}</span>
            </div>
          )}
        </div>
      </div>
    </WizardLayout>
  );
}

function MethodCard({ active, onClick, icon, title, sub }: { active: boolean; onClick: () => void; icon: React.ReactNode; title: string; sub: string }) {
  return (
    <button onClick={onClick}
      className={cn("text-left p-4 rounded-md grid gap-2 transition-colors",
        active ? "bg-cream border-2 border-ink" : "bg-white border border-ink-12")}>
      {icon}
      <div className="font-display font-bold text-[14px]">{title}</div>
      <div className="text-[12px] text-ink-40">{sub}</div>
    </button>
  );
}

function SumRow({ k, v, muted, accent }: { k: string; v: string; muted?: boolean; accent?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-ink-40" : accent ? "text-[#27500A]" : "text-ink-60"}>{k}</span>
      <span className={cn("font-mono font-semibold", accent && "text-[#27500A]")}>{v}</span>
    </div>
  );
}
